import React from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { di } from '../../../../state/inversify.config';
import { AssessmentSummaryHelper } from './summary.helper';
import { ModulesSpecsDomain } from '../takeAssessment/modules/modules.domain';
import { ModuleType } from '../takeAssessment/modules/modules.interface';
import { useCustomTranslation } from '../../../../translation/customTranslation';
import { SummaryRow } from './summaryRow.dumb';

interface SummaryTableProps {
  modules: ModuleType;
}

interface ModuleConfig {
  key: string;
  headingKey: string;
  subHeadingKey?: string;
}

export function SummaryTable({ modules }: SummaryTableProps) {
  const { t } = useCustomTranslation('assessment');

  // TODO: make these pure functions instead of a singleton
  const assessmentSummaryHelper: AssessmentSummaryHelper =
    di.get<AssessmentSummaryHelper>(AssessmentSummaryHelper);

  const moduleSpecsDomain: ModulesSpecsDomain =
    di.get<ModulesSpecsDomain>(ModulesSpecsDomain);

  const moduleConfigurations: ModuleConfig[] = [
    {
      key: 'glasgowComaScale',
      headingKey: moduleSpecsDomain.glasgowCommaScaleConfig().heading,
    },
    {
      key: 'immediateMemory',
      headingKey: moduleSpecsDomain.immediateMemoryExamConfig().heading,
    },
    {
      key: 'symptomsChecklist',
      headingKey: moduleSpecsDomain.symptomsChecklistConfig().heading,
    },
    {
      key: 'digitsBackwards',
      headingKey: moduleSpecsDomain.digitsBackwardsConfig().heading,
    },
    {
      key: 'monthsInReverse',
      headingKey: moduleSpecsDomain.monthsInReverseExamConfig().heading,
    },
    {
      key: 'balanceTandemGait',
      headingKey: moduleSpecsDomain.balanceTandemGaitExamConfig().heading,
      subHeadingKey: moduleSpecsDomain.balanceTandemGaitExamConfig().subHeading,
    },
    {
      key: 'balanceDoubleLegStance',
      headingKey: moduleSpecsDomain.doubleLegBalanceExamConfig().heading,
      subHeadingKey: moduleSpecsDomain.doubleLegBalanceExamConfig().subHeading,
    },
    {
      key: 'balanceSingleLegStance',
      headingKey: moduleSpecsDomain.singleLegBalanceExamConfig().heading,
      subHeadingKey: moduleSpecsDomain.singleLegBalanceExamConfig().subHeading,
    },
    {
      key: 'balanceTandemStance',
      headingKey: moduleSpecsDomain.tandemBalanceExamConfig().heading,
      subHeadingKey: moduleSpecsDomain.tandemBalanceExamConfig().subHeading,
    },
    {
      key: 'delayedRecall',
      headingKey: moduleSpecsDomain.delayedRecallExamConfig().heading,
    },
    { key: 'neckExam', headingKey: moduleSpecsDomain.neckExamConfig().heading },
    {
      key: 'neurologicalExam',
      headingKey: moduleSpecsDomain.neurologicalExamConfig().heading,
    },
    {
      key: 'vestibularOcularMotorScreen',
      headingKey: 'ID_VOMS_TITLE_ASSESSMENT_SHORT',
    },
    {
      key: 'treatmentPlan',
      headingKey: moduleSpecsDomain.treatmentPlanExamConfig().heading,
    },
  ];

  return (
    <Grid container spacing={2} sx={{ my: 2 }}>
      <Grid item container component={Paper} justifyContent="space-between">
        <Grid item xs={3}>
          <Typography variant="h6">
            {t('ASSESSMENT_SUMMARY_COL_HEADER_STATUS')}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="h6">
            {t('ASSESSMENT_SUMMARY_COL_HEADER_MODULE_NAME')}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="h6">
            {t('ASSESSMENT_SUMMARY_COL_HEADER_NORMATIVE_SUMMARY')}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="h6">
            {t('ASSESSMENT_SUMMARY_COL_HEADER_CLINICAL_NOTES')}
          </Typography>
        </Grid>
      </Grid>
      {moduleConfigurations.map((config) => (
        <SummaryRow
          key={config.key}
          module={modules}
          config={config}
          assessmentSummaryHelper={assessmentSummaryHelper}
          translate={t}
        />
      ))}
    </Grid>
  );
}
